
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fromTable } from '@/integrations/supabase/client';

export interface FeedbackRequest {
  id: string;
  user_id: string;
  candidate_name?: string;
  candidate_email?: string;
  candidate_user_id?: string;
  recruiter_name?: string;
  recruiter_email?: string;
  recruiter_company?: string;
  recruiter_user_id?: string;
  job_title?: string;
  message?: string;
  created_at: string;
  updated_at?: string;
  status?: string;
  unique_token?: string;
  feedback_id?: string;
  context?: string;
  direction?: string;
  last_reminder_sent?: string;
}

export const useFeedbackRequest = (requestId?: string) => {
  const [request, setRequest] = useState<FeedbackRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequest = async () => {
      if (!requestId) {
        setLoading(false);
        setError("Request ID not provided");
        return;
      }

      try {
        setLoading(true);
        
        // First, try to fetch from regular feedback_requests
        const { data: feedbackRequest, error: requestError } = await supabase
          .from('feedback_requests')
          .select('*')
          .eq('id', requestId)
          .maybeSingle();
        
        if (requestError && requestError.code !== 'PGRST116') {
          // PGRST116 is "row not found" error, which we want to handle by checking the other table
          throw requestError;
        }
        
        if (feedbackRequest) {
          // Créer un objet avec les propriétés de base et les propriétés additionnelles
          const enhancedRequest: FeedbackRequest = {
            ...(feedbackRequest as any),
            context: (feedbackRequest as any).context || 'recruitment',
            direction: (feedbackRequest as any).direction || 'recruiter_to_candidate'
          };
          setRequest(enhancedRequest);
          setLoading(false);
          return;
        }
        
        // If not found in main table, check candidate_to_recruiter_feedback_requests
        const { data: candidateRequest, error: candidateError } = await supabase
          .from('candidate_to_recruiter_feedback_requests')
          .select('*')
          .eq('id', requestId)
          .maybeSingle();
        
        if (candidateError) {
          throw candidateError;
        }
        
        if (candidateRequest) {
          // Créer un objet avec les propriétés de base et les propriétés additionnelles
          const enhancedRequest: FeedbackRequest = {
            ...(candidateRequest as any),
            context: (candidateRequest as any).context || 'recruitment',
            direction: (candidateRequest as any).direction || 'candidate_to_recruiter'
          };
          setRequest(enhancedRequest);
        } else {
          setError("Request not found");
        }
      } catch (error) {
        console.error("Error fetching feedback request:", error);
        setError("Failed to fetch request details");
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [requestId]);

  return { request, loading, error };
};
